// =============================================================================
// MOCK FETCH — interceptor de window.fetch para la demo offline
// -----------------------------------------------------------------------------
// Al llamar instalarMockFetch() pisamos window.fetch con un router que
// reconoce los endpoints del backend y devuelve datos del mockup. Las URLs
// no reconocidas caen al fetch original (por si la demo necesita salir a la
// red para algun asset estatico).
//
// Para apagar la demo: llamar desinstalarMockFetch().
// =============================================================================

import {
  DEMO_LOGIN_CREDENTIALS,
  DEMO_USUARIO,
  DEMO_EMPRESA_COLORES,
  DEMO_SURTIDORES,
  DEMO_CONDUCTORES,
  DEMO_VEHICULOS,
  DEMO_USUARIOS_SISTEMA,
  DEMO_TIPOS_COMBUSTIBLE,
  DEMO_CARGAS,
  DEMO_DASHBOARD_LAYOUT,
  generarSensoresCombustible,
  generarConsumoPorVehiculo,
} from "./demoData";

const DELAY_MS = 180; // simula latencia para que la UI no parezca rota

// Mutable in-memory state — para que insertar/editar/borrar tenga efecto
// visible durante la demo sin tocar el archivo.
const state = {
  surtidores: [...DEMO_SURTIDORES],
  usuariosSistema: [...DEMO_USUARIOS_SISTEMA],
  cargas: [...DEMO_CARGAS],
  dashboardLayout: JSON.parse(JSON.stringify(DEMO_DASHBOARD_LAYOUT)),
};

let originalFetch = null;
let instalado = false;

// ---------- helpers ----------
function jsonResponse(payload, init = {}) {
  return new Response(JSON.stringify(payload), {
    status: init.status || 200,
    headers: { "Content-Type": "application/json", ...(init.headers || {}) },
  });
}

function wait(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

async function parseBody(init) {
  if (!init || !init.body) return {};
  try {
    if (typeof init.body === "string") return JSON.parse(init.body);
    if (init.body instanceof FormData) {
      const obj = {};
      init.body.forEach((v, k) => (obj[k] = v));
      return obj;
    }
    return {};
  } catch {
    return {};
  }
}

function urlIncluye(url, fragmento) {
  return String(url).indexOf(fragmento) !== -1;
}

function nuevoId() {
  return Math.floor(Date.now() / 1000) + Math.floor(Math.random() * 1000);
}

// El reporte RP007 recibe la fecha como mesyymm="YYMM" + dia + hora + min.
function parseMesDiaParaUnix(mesyymm, dia, hora, min) {
  if (!mesyymm || String(mesyymm).length < 4) return 0;
  const s  = String(mesyymm);
  const yy = 2000 + Number(s.slice(0, 2));
  const mm = Number(s.slice(2, 4)) - 1;
  const d  = Number(dia  || 1);
  const h  = Number(hora || 0);
  const m  = Number(min  || 0);
  return Math.floor(new Date(yy, mm, d, h, m, 0).getTime() / 1000);
}

// ---------- routing ----------
async function handle(url, init = {}) {
  const method = (init.method || "GET").toUpperCase();
  const body = await parseBody(init);

  // ===== AUTENTICACION =====
  if (urlIncluye(url, "/autenticacion/login")) {
    const ok =
      body.usuario === DEMO_LOGIN_CREDENTIALS.usuario &&
      body.pass === DEMO_LOGIN_CREDENTIALS.pass;
    if (!ok) {
      return jsonResponse({ code: "FAILURE", body: null });
    }
    return jsonResponse({ code: "SUCCESS", body: DEMO_USUARIO });
  }

  // ===== COLORES / EMPRESA =====
  if (urlIncluye(url, "/configuracion-empresa-cliente/obtener-configuracion-empresa-cliente")) {
    return jsonResponse({ code: "SUCCESS", body: [DEMO_EMPRESA_COLORES] });
  }
  if (urlIncluye(url, "/configuracion-empresa-cliente/actualizar-idioma-empresa-cliente")) {
    return jsonResponse({ code: "SUCCESS", body: true });
  }

  // ===== VEHICULOS / PLACAS =====
  if (
    urlIncluye(url, "/api/vehiculo/obtener-placas") ||
    urlIncluye(url, "/api/vehiculo/obtener-placas-subcuenta")
  ) {
    const placas = DEMO_VEHICULOS.map((v) => ({
      imei: v.imei,
      placa: v.placa,
      ID_ACTIVO: v.placa,
    }));
    return jsonResponse({ code: "SUCCESS", body: placas });
  }

  // ===== CONDUCTORES =====
  if (urlIncluye(url, "/api/conductor/obtener-options-conductores")) {
    return jsonResponse({ code: "SUCCESS", body: DEMO_CONDUCTORES });
  }

  // ===== DASHBOARD LAYOUT =====
  if (urlIncluye(url, "/api/dashboard-layouts/")) {
    if (method === "GET") {
      return jsonResponse(state.dashboardLayout);
    }
    if (method === "POST") {
      // body trae { bp, layout, items }
      const { bp, layout, items } = body;
      if (bp && Array.isArray(layout)) {
        state.dashboardLayout.layouts[bp] = layout;
      }
      if (Array.isArray(items)) {
        state.dashboardLayout.items = items;
      }
      return jsonResponse({ code: "SUCCESS" });
    }
  }

  // ===== SURTIDORES =====
  if (urlIncluye(url, "/combustibleapi/obtenerListaSurtidores")) {
    return jsonResponse(state.surtidores);
  }
  if (urlIncluye(url, "/combustibleapi/eliminarSurtidor")) {
    const id = Number(body.surtidorId);
    state.surtidores = state.surtidores.filter((s) => Number(s.ID_SURTIDOR) !== id);
    return jsonResponse(state.surtidores);
  }
  if (urlIncluye(url, "/combustibleapi/registrarSurtidor")) {
    state.surtidores.push({
      ID_SURTIDOR: nuevoId(),
      NOMBRE: body.nombre || "NUEVO SURTIDOR",
      CIUDAD: body.ciudad || "N/A",
    });
    return jsonResponse(state.surtidores);
  }

  // ===== USUARIOS DEL SISTEMA =====
  if (urlIncluye(url, "/combustibleapi/registrarUsuario")) {
    const existe = state.usuariosSistema.find((u) => u.USERNAME === body.username);
    if (existe) return jsonResponse({ code: "ER_DUP_ENTRY" });
    state.usuariosSistema.push({
      ID_USUARIO: nuevoId(),
      NOMBREUSUARIO: body.nombreusuario,
      USERNAME: body.username,
      PASSWORD: body.password,
      ID_CONDUCTOR: body.conductorId || null,
      ELIMINADO: 0,
    });
    return jsonResponse(state.usuariosSistema);
  }

  // ===== CARGAS DE COMBUSTIBLE =====
  if (urlIncluye(url, "/combustibleapi/getListaCargas")) {
    // body: { token, empresaId, utimeini, utimefin }
    const ini = Number(body.utimeini || 0);
    const fin = Number(body.utimefin || Math.floor(Date.now() / 1000));
    const filtradas = state.cargas.filter((c) => {
      const ts = Math.floor(new Date(c.FECHAHORA_CARGA).getTime() / 1000);
      return ts >= ini && ts <= fin;
    });
    return jsonResponse(filtradas);
  }
  if (urlIncluye(url, "/combustibleapi/editar_carga")) {
    const id = String(body.idCarga);
    const idx = state.cargas.findIndex((c) => String(c.ID_CARGA) === id);
    if (idx >= 0) {
      const c = state.cargas[idx];
      const surt = state.surtidores.find((s) => Number(s.ID_SURTIDOR) === Number(body.surtidor));
      state.cargas[idx] = {
        ...c,
        MEDIDOR: body.medidor ?? c.MEDIDOR,
        CANTIDAD: body.cantidad ?? c.CANTIDAD,
        CONSUMO: body.costoTotal ?? c.CONSUMO,
        OBSERVACIONES: body.observaciones ?? c.OBSERVACIONES,
        ELIMINADO: body.deshabilitado ?? c.ELIMINADO,
        SURTIDOR: surt ? surt.NOMBRE : c.SURTIDOR,
        ID_SURTIDOR: surt ? surt.ID_SURTIDOR : c.ID_SURTIDOR,
        ID_TIPO_COMBUSTIBLE: body.tipoCombustibleID ?? c.ID_TIPO_COMBUSTIBLE,
        NRO_FACTURA: body.nroOrden ?? c.NRO_FACTURA,
      };
    }
    return jsonResponse({ status: "OK" });
  }

  // ===== /api/datos — ENDPOINT MULTI-OP =====
  if (urlIncluye(url, "/api/datos")) {
    const op = body.op || "";
    const dataOp = body.data_op || {};

    if (op === "READ-OBTENERTODOSLOSUSUARIOSCOMBUSTIBLE") {
      return jsonResponse({ data: state.usuariosSistema });
    }

    if (op === "READ-TIPOCOMBUSTIBLE") {
      return jsonResponse({
        data: DEMO_TIPOS_COMBUSTIBLE.map((t) => ({
          idTipoCombustible: t.id,
          nombre: t.label,
        })),
      });
    }

    if (op === "READ-VEHICULOEMPRESA") {
      const rows = DEMO_VEHICULOS.map((v) => ({
        placa: v.placa,
        nombre: v.placa,
        idTipoVehiculo: 1,
        idMarca: 0,
        marca: v.marca,
        idModelo: 0,
        modelo: v.modelo,
        anno: v.anno,
        habilitado: 1,
        capCarga: v.capacidadTanque,
        capTanque: v.capacidadTanque,
        idTipoCombustible: v.tipoCombustibleId,
        idVoltajeBateria: 0,
        voltajeBateria: 12,
        idTraccion: 0,
        urlFoto: "",
        color: "BLANCO",
        cilindrada: 2400,
        chasis: `CH-${v.imei.slice(-6)}`,
        nroMotor: `MT-${v.imei.slice(-5)}`,
        rendimientoKm: 10,
      }));
      return jsonResponse({ data: rows });
    }

    if (op === "READ-MEDIDORULTIMACARGA") {
      const placa = dataOp.idActivo;
      const ultima = state.cargas
        .filter((c) => c.ACTIVO === placa)
        .sort((a, b) => (a.FECHAHORA_CARGA < b.FECHAHORA_CARGA ? 1 : -1))[0];
      return jsonResponse({
        error: false,
        data: ultima ? [{ MEDIDOR: ultima.MEDIDOR }] : [],
      });
    }

    if (op === "CREATE-CARGACOMBUSTIBLE") {
      const surt = state.surtidores.find((s) => Number(s.ID_SURTIDOR) === Number(dataOp.surtidorID));
      const veh = DEMO_VEHICULOS.find((v) => v.placa === dataOp.vehiculoID || v.imei === dataOp.vehiculoID);
      state.cargas.unshift({
        ID_CARGA: String(dataOp.idCarga || nuevoId()),
        FECHAHORA_CARGA: new Date(dataOp.fechaHoraCarga * 1000)
          .toISOString()
          .replace("T", " ")
          .slice(0, 19),
        ACTIVO: veh ? veh.placa : (dataOp.vehiculoID || ""),
        DESCRIPCION: veh ? `${veh.marca} ${veh.modelo}` : "",
        USUARIO: DEMO_USUARIO.nombreUsuario,
        CONDUCTOR: dataOp.conductorNombre || "",
        ID_CONDUCTOR: dataOp.conductorID,
        SURTIDOR: surt ? surt.NOMBRE : "",
        ID_SURTIDOR: dataOp.surtidorID,
        tipoCombustible:
          (
            ["", "GASOLINA", "DIESEL", "GASOLINA SUPER ETANOL 92", "GASOLINA PREMIUM", "GNV"][
              Number(dataOp.tipoCombustibleID) || 0
            ] || ""
          ),
        ID_TIPO_COMBUSTIBLE: dataOp.tipoCombustibleID,
        CANTIDAD: dataOp.cantidad,
        CONSUMO: dataOp.costoTotal,
        MEDIDOR: dataOp.medidor,
        CARGA_A_FULL: dataOp.cargaFull ? 1 : 0,
        NRO_FACTURA: dataOp.nroOrden || "",
        OBSERVACIONES: dataOp.observaciones || "",
        URL_FOTO_MEDIDOR: dataOp.urlFotoMedidor || "uploads/demo-placeholder.jpg",
        CODIGO_QR: "",
        ELIMINADO: 0,
      });
      return jsonResponse({ error: false });
    }

    if (op === "UPDATE-USUARIOCOMBUSTIBLE") {
      const idx = state.usuariosSistema.findIndex(
        (u) => Number(u.ID_USUARIO) === Number(dataOp.id),
      );
      if (idx >= 0) {
        state.usuariosSistema[idx] = {
          ...state.usuariosSistema[idx],
          NOMBREUSUARIO: dataOp.nombreUsuario,
          USERNAME: dataOp.username,
          PASSWORD: dataOp.pasword,
          ID_CONDUCTOR: dataOp.idConductor,
        };
      }
      return jsonResponse({ error: false });
    }

    if (op === "UPDATE-HABILITARUSUARIOCOMBUSTIBLE") {
      const idx = state.usuariosSistema.findIndex(
        (u) => Number(u.ID_USUARIO) === Number(dataOp.id),
      );
      if (idx >= 0) {
        state.usuariosSistema[idx].ELIMINADO = Number(dataOp.eliminado);
      }
      return jsonResponse({ error: false });
    }

    // op desconocido — devolvemos shape vacio para no romper la UI
    return jsonResponse({ error: false, data: [] });
  }

  // ===== SENSORES (RP003BodyV11.rep) =====
  if (urlIncluye(url, "/reportes/RP003BodyV11.rep")) {
    const u = new URL(url, "https://x.local");
    const imei = u.searchParams.get("IMEI");
    const veh = DEMO_VEHICULOS.find((v) => v.imei === imei);
    const placa = veh ? veh.placa : imei || "DEMO";
    return jsonResponse({ data: generarSensoresCombustible(placa) });
  }

  // ===== REPORTE CONSUMO POR VEHICULO (RP007BodyV02.rep) =====
  if (urlIncluye(url, "/reportes/RP007BodyV02.rep")) {
    const u = new URL(url, "https://x.local");
    const desdeUnix = parseMesDiaParaUnix(
      u.searchParams.get("mesi"),
      u.searchParams.get("diai"),
      u.searchParams.get("horai"),
      u.searchParams.get("mini"),
    );
    const hastaUnix = parseMesDiaParaUnix(
      u.searchParams.get("mesf"),
      u.searchParams.get("diaf"),
      u.searchParams.get("horaf"),
      u.searchParams.get("minf"),
    );
    return jsonResponse({
      data: generarConsumoPorVehiculo({
        desdeUnix,
        hastaUnix,
        placa:                u.searchParams.get("IMEI")         || "",
        tipoCombustibleLabel: u.searchParams.get("vCombustible") || "",
        idSurtidor:           u.searchParams.get("vidSur")       || "",
      }),
    });
  }

  // ===== Fallthrough — pasa al fetch original =====
  return originalFetch(url, init);
}

// -----------------------------------------------------------------------------
// API publica
// -----------------------------------------------------------------------------
export function instalarMockFetch() {
  if (instalado) return;
  originalFetch = window.fetch.bind(window);
  window.fetch = async function patchedFetch(input, init) {
    const url = typeof input === "string" ? input : (input && input.url) || "";
    await wait(DELAY_MS);
    try {
      return await handle(url, init || {});
    } catch (e) {
      console.error("[mockFetch] error en handler:", e);
      return originalFetch(input, init);
    }
  };
  instalado = true;
  console.log("%c[DEMO] mockFetch instalado", "color:#1f4e8c;font-weight:bold;");
}

export function desinstalarMockFetch() {
  if (!instalado || !originalFetch) return;
  window.fetch = originalFetch;
  originalFetch = null;
  instalado = false;
  console.log("[DEMO] mockFetch desinstalado");
}

export function estaInstaladoMockFetch() {
  return instalado;
}

// Resetea las mutaciones que se hicieron durante la demo, para volver al
// dataset inicial sin reload.
export function resetMockState() {
  state.surtidores = [...DEMO_SURTIDORES];
  state.usuariosSistema = [...DEMO_USUARIOS_SISTEMA];
  state.cargas = [...DEMO_CARGAS];
  state.dashboardLayout = JSON.parse(JSON.stringify(DEMO_DASHBOARD_LAYOUT));
}
